import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type SupportPanelView = 'chat' | 'message';

interface SupportPanelContextValue {
  isOpen: boolean;
  view: SupportPanelView;
  /** Opens the panel, optionally straight to a particular view. */
  openSupport: (view?: SupportPanelView) => void;
  closeSupport: () => void;
  setView: (view: SupportPanelView) => void;
}

/**
 * Safe default so a component can call `openSupport` without caring whether a
 * provider is above it — matching AppUpdateContext. The calls are no-ops, which
 * degrades to "nothing happens" rather than crashing a page.
 */
const DEFAULT: SupportPanelContextValue = {
  isOpen: false,
  view: 'chat',
  openSupport: () => {},
  closeSupport: () => {},
  setView: () => {},
};

const SupportPanelContext = createContext<SupportPanelContextValue>(DEFAULT);

/**
 * Holds the support panel's open state outside the button that usually opens it.
 *
 * The panel lived entirely inside SupportBotButton, so it could only ever be
 * opened by pressing that button. Copy that tells someone to contact support —
 * a declined identity check, for instance — then had no way to take them there,
 * leaving them to find a help icon in the header and guess which of its tabs was
 * meant. Lifting the state lets a page open the panel on the right view.
 */
export function SupportPanelProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<SupportPanelView>('chat');

  const openSupport = useCallback((next?: SupportPanelView) => {
    if (next) setView(next);
    setIsOpen(true);
  }, []);

  const closeSupport = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, view, openSupport, closeSupport, setView }),
    [isOpen, view, openSupport, closeSupport],
  );

  return <SupportPanelContext.Provider value={value}>{children}</SupportPanelContext.Provider>;
}

export function useSupportPanel(): SupportPanelContextValue {
  return useContext(SupportPanelContext);
}
